import {api} from '@/helpers/api';
import {submitStatus, USER_KEY} from '@/helpers/utils';
import {
    AUTH_MUTATIONS,
    HOME_MUTATIONS,
    SEARCH_MUTATIONS,
    PERMISSIONS_MUTATIONS,
    BLOG_MUTATIONS
} from './constants'

const PAGINATION_LIMIT = 9;
export default {
    // Auth actions
    storeLoggedInUser: (context, user) => {
        context.commit(AUTH_MUTATIONS.UPDATE_USER, user);
    },
    fetchUserSettings: (context, user) => {
        context.commit(AUTH_MUTATIONS.USER_SETTINGS_LOADING, true);
        return api.getUserSettings(user).then(result => {
            context.commit(AUTH_MUTATIONS.UPDATE_USER_SETTINGS, result);
            context.commit(AUTH_MUTATIONS.USER_SETTINGS_LOADING, false);
        })
    },
    updateUserSettings: (context, userSettings) => {
        api.updateUserSettings(userSettings).then(result => {
            context.commit(AUTH_MUTATIONS.UPDATE_USER_SETTINGS, result);
        })
    },
    clearUserDetails: (context) => {
        context.commit(AUTH_MUTATIONS.UPDATE_USER, {});
        context.commit(AUTH_MUTATIONS.UPDATE_USER_SETTINGS, {});
    },
    getUsersWhoConsentedToSharing: (context) => {
       api.getShareableUserIds().then(result => {
           context.commit(AUTH_MUTATIONS.UPDATE_SHAREABLE_USERS, result);
       })
    },

    // Search actions
    resetSearch(context) {
        context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, null);
        context.commit(SEARCH_MUTATIONS.SET_SEARCH_RESULTS, []);
    },
    searchForHome(context, searchQuery) {
        context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.LOADING);

        // get most recent search result if previous pages loaded
        let previous = null;
        const shouldGetNextPage = parseInt(searchQuery.nextPage) === 1;
        const shouldPaginate = searchQuery.shouldPaginate;
        const userSettings = context.getters.userWithSettings;
        const userIds = userSettings ? [userSettings.user_id, userSettings.sub].filter(id => id) : null;

        if (shouldGetNextPage) {
            const oldSearchResults = context.state.searchResults;
            const mostRecentItem = oldSearchResults[oldSearchResults.length - 1];
            previous = mostRecentItem._id;
        }

        api.searchForHomes(searchQuery.query, previous, shouldPaginate, {ids: userIds}).then(result => {
            if ('errors' in result) {
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.FAILED);
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_RESULTS, []);
            } else {
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.SUCCESS);
                const resultsArray = shouldGetNextPage ? [...context.state.searchResults, ...result.homes] : result.homes;
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_RESULTS, resultsArray);

                const totalSearchResults = result.numSearchResults;
                if (previous === null) {
                    // fresh search, set result size
                    context.commit(SEARCH_MUTATIONS.SET_NUM_SEARCH_RESULTS, totalSearchResults);
                }

                const moreToLoad = result.homes && result.homes.length === PAGINATION_LIMIT
                    && totalSearchResults > resultsArray.length;
                context.commit(SEARCH_MUTATIONS.SET_MORE_TO_LOAD, moreToLoad);
            }
        })
    },
    getRecentHomes(context, limit = 4) {
        context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.LOADING);
        api.getRecentHomes(limit).then(result => {
            if ('error' in result) {
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.FAILED);
            } else {
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.SUCCESS);
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_RESULTS, result);
            }
        })
    },
    getHomeById(context, {id, isEdit}) {
        context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.LOADING);
        const userSettings = context.getters.userWithSettings;
        const userIds = userSettings ? [userSettings.user_id, userSettings.sub].filter(id => id) : null;
        api.getHomeById(id, {ids: userIds}).then(result => {
            if (result.error) {
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.FAILED);
            } else {
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_STATUS, submitStatus.SUCCESS);
                context.commit(SEARCH_MUTATIONS.SET_SEARCH_RESULTS, result);
                if (isEdit) {
                    context.commit(HOME_MUTATIONS.SET_HOME_TO_EDIT, result);
                }
            }
        });
    },
    // blog management
    getBlogPosts(context, params) {
        context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, submitStatus.LOADING);

        const shouldGetNextPage = parseInt(params.nextPage) === 1;
        let previous = null;

        if (shouldGetNextPage) {
            const oldBlogPosts = context.state.blogPosts;
            const lastItem = oldBlogPosts[oldBlogPosts.length - 1];
            previous = lastItem._id;
        }
        return api.getBlogPosts(previous).then(result => {
            const errorThrown = 'error' in result;
            const posts = errorThrown ? [] : result.blogPosts;
            const resultsList = previous === null ? posts : [...context.state.blogPosts, ...posts];
            context.commit(BLOG_MUTATIONS.UPDATE_BLOG_POST_LIST, resultsList);
            context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, errorThrown ? submitStatus.FAILED : submitStatus.SUCCESS);

            const moreToLoad = posts.length === 4 && result.numResults > resultsList.length;
            context.commit(SEARCH_MUTATIONS.SET_MORE_TO_LOAD, moreToLoad);
        })
    },
    publishBlogPost(context, postContents) {
        context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, submitStatus.LOADING);
        api.publishBlogPost(postContents).then(result => {
            const status = result.errors ? submitStatus.FAILED : submitStatus.SUCCESS;
            context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, status);
        })
    },
    updateBlogPost(context, { id, postContents }) {
        context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, submitStatus.LOADING);
        api.updateBlogPost(id, postContents).then(result => {
            const status = result.errors ? submitStatus.FAILED : submitStatus.SUCCESS;
            context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, status);
        })
    },
    deleteBlogPost: (context, postId) => {
        context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, submitStatus.LOADING);
        api.deleteBlogPost(postId).then(result => {
            if (result.error) {
                context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, submitStatus.FAILED);
            } else {
                context.commit(BLOG_MUTATIONS.SET_BLOG_STATUS, submitStatus.SUCCESS);
            }
        })
    },
    setPostToEdit: (context, post) => {
        context.commit(BLOG_MUTATIONS.SET_POST_TO_EDIT, post);
    }
}

